import React, {
  useContext,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import ActiveStatusDialog from "./ActiveStatusDialog";
import FormComponent from "./Form";
import { SelectUserDialog } from "./SelectUserDialog";
import { PaginatedPost, PaginatedPostsParams } from "../log.typing";
import { AOO_API_HOST } from "../../../../env";
import { useSignalRConnection } from "./useSignalRConnection";
import Drawer from "./SwipeableDrawer";
import { fetchMachineData } from "../api/actions";
import { LogMachineData } from "../log.typing";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import {
  Overlay,
  Dialog,
  Header,
  CloseButton,
  Cover,
  BackButton,
} from "../style/LogStyles";
import { GlobalContext } from "../../../../context/GlobalContext";
import { LogContext, initialPostData } from "../../../../context/LogContext";
import ConfirmDelete from "./ConfirmDelete";
import { PostsByDate } from "./PostsByDate";
import { PostTimeline } from "./Timeline/PostTimeline";
import { isBrowser, isMobile } from "react-device-detect";
import { HubConnectionState } from "@microsoft/signalr";
import { SlackSearchResultsContext } from "../../../Slack/Context/SlackSearchResultsContext";
import { EditMachineButton } from "../../../Slack/MachineEditor/EditMachineButton";
import { MachineQrCodeButton } from "../../../Slack/MachineEditor/MachineQrCodeButton";

const Log = () => {
  const {
    setMachineId,
    setMenuClicked,
    setisNavActive,
    backtoMenu,
    setBacktoMenu,
  } = useContext(GlobalContext);
  const { log, setLog, eventOption, machineId } = useContext(GlobalContext);
  const {
    id,
    setReset,
    setPostData,
    setPosts,
    posts,
    activePage,
    setActivePage,
    totalPages,
    fetchData,
    PAGE_SIZE,
    isLoading,
    selectedImageData,
    getImageUrl,
  } = useContext(LogContext);

  const slackSearchResultsContext = useContext(SlackSearchResultsContext); //be careful about this one, dirty hack
  const [machine, setMachine] = useState<LogMachineData>();
  const [event, setEvent] = useState<boolean>(false);
  const [statusOpen, setStatusOpen] = useState<boolean>(false);
  const [isInit, setIsInit] = useState<boolean>(false);
  const [img, setImg] = useState("");
  const postsRef = useRef(null);
  const connection = useSignalRConnection(AOO_API_HOST + "/api/log", machineId);
  const collectRequestParams = useCallback(() => {
    return {
      MachineId: machineId,
      PageNumber: activePage,
      PageSize: PAGE_SIZE.current,
    };
  }, [PAGE_SIZE, machineId, activePage]);

  const getPosts = useCallback(
    (params: PaginatedPostsParams) => {
      if (params?.MachineId?.length > 0 && !isLoading) {
        fetchData("GetPaginatedLog", params).catch((error: Error) => {
          console.error("Error while fetching posts:", error);
        });
      }
    },
    [fetchData, isLoading]
  );

  const connectToHub = useCallback(
    async (id: string) => {
      try {
        if (id) {
          await connection.start();
          await connection.invoke("Join", id);
        }
      } catch (e) {
        console.error(e);
      }
    },
    [connection]
  );

  const closeConnection = useCallback(async () => {
    try {
      if (connection) {
        await connection.stop();
        connection.off(); // Remove any remaining event handlers
      }
    } catch (e) {
      console.error(e);
    }
  }, [connection]);

  const loadOlderPosts = useCallback(() => {
    getPosts({ ...collectRequestParams(), PageNumber: activePage + 1 });
  }, [activePage, collectRequestParams, getPosts]);

  const getMap = useCallback(() => {
    if (!postsRef.current) {
      // Initialize the Map on first usage.
      postsRef.current = new Map();
    }
    return postsRef.current;
  }, []);

  useEffect(() => {
    const fetchDataAsync = async () => {
      try {
        if (machineId) {
          // Check if machineId exists
          const userData = await fetchMachineData(machineId);
          setMachine(userData);
        }
      } catch (error) {
        // Handle error
        console.error(error);
      }
    };
    // reset page after selected machine change
    fetchDataAsync();
    return () => {
      setActivePage(1);
      setIsInit(false);
      setPosts([]);
    };
  }, [machineId, setActivePage, setPosts]);

  useEffect(() => {
    if (
      machine !== null &&
      machine?.Cover !== undefined &&
      machine?.Cover !== null
    ) {
      if (machine.Cover.ImageData) {
        setImg(
          `data:${machine.Cover.Content};base64,${machine.Cover.ImageData}`
        );
      } else {
        setImg(getImageUrl(machine.Cover.ContentId));
      }
    } else {
      setImg(null);
    }
  }, [machine, getImageUrl]);

  useEffect(() => {
    if (connection && connection.state === HubConnectionState.Disconnected) {
      connectToHub(machineId);
    }
  }, [connection, connectToHub, machineId]);

  useEffect(() => {
    if (connection) {
      // Remove all event handlers
      connection.off();

      connection.on("ReceiveMessage", (jsonData) => {
        console.debug(jsonData);
      });

      connection.on("ItemCreated", (jsonData) => {
        const dataObject: PaginatedPost = JSON.parse(jsonData);
        setPosts((prevItems) =>
          // Posts are keyed by PostId, so a broadcast for a post already on screen - one a page
          // fetch returned first - must not add it a second time.
          prevItems.some((item) => item.PostId === dataObject.PostId)
            ? prevItems
            : [...prevItems, dataObject]
        );
      });

      connection.on("ItemRemoved", (deletedItemId) => {
        const dataObject = JSON.parse(deletedItemId);
        setPosts((prevItems) =>
          prevItems.filter((item) => item.PostId !== dataObject)
        );
      });

      connection.on("ItemUpdated", (jsonData) => {
        const dataObject: PaginatedPost = JSON.parse(jsonData);

        setPosts((prevData) => {
          const updatedData: PaginatedPost[] = prevData.map((item) => {
            if (item.PostId === dataObject.PostId) {
              item.Action = dataObject.Action;
              item.Observation = dataObject.Observation;
              item.Symptoms = dataObject.Symptoms;
              item.Hypothesis = dataObject.Hypothesis;
              item.Plan = dataObject.Plan;
              item.ImprovementProposition = dataObject.ImprovementProposition;
              item.Question = dataObject.Question;
              item.Comment = dataObject.Comment;
              item.Images = dataObject.Images;

              return {
                ...item,
                ...dataObject,
              };
            }
            return item;
          });

          return updatedData;
        });
      });
    }
  }, [connection, setPosts]);


  useEffect(() => {
    //setPosts([]);
    const collectedParams = collectRequestParams();
    if (
      !isInit &&
      collectedParams.MachineId &&
      collectedParams.MachineId.length > 0
    ) {
      const params = {
        ...collectedParams,
        PageNumber: activePage,
      };
      getPosts(params);
      setIsInit(true);
    }
  }, [collectRequestParams, setIsInit, activePage, getPosts, isInit]);


  // Add useEffect to disable overscroll (pull-to-refresh) behavior
  useEffect(() => {
    // Save the original value to restore it later
    const originalOverscrollBehavior =
      document.documentElement.style.overscrollBehavior;

    // Disable pull-to-refresh
    document.documentElement.style.overscrollBehavior = "none";

    // Restore original value on component unmount
    return () => {
      document.documentElement.style.overscrollBehavior =
        originalOverscrollBehavior;
    };
  }, []);

  const backToMachines = () => {
    setMachineId(null);
    setLog(false);
    setBacktoMenu(false);
    setIsInit(false);
    setPosts([]);
    setActivePage(1);

    if (slackSearchResultsContext) {
      // The slack view has its own machine list sitting behind the log, and no nav panel. Reopening
      // the navbar menu here would drop it on top of that list.
      slackSearchResultsContext.fetchMachineLogs();
      return;
    }

    // Only the main view came from the navbar machine list, so only it goes back to one.
    setMenuClicked(true);
    setisNavActive(true);
  };
  return (
    <Overlay
      position={slackSearchResultsContext && !isMobile ? "initial" : "fixed"}
      display={log ? "flex" : "none"}
    >
      <SelectUserDialog />
      <ConfirmDelete postId={id} />
      <ActiveStatusDialog
        option={eventOption}
        statusOpen={statusOpen}
        setEvent={setEvent}
        setLog={setLog}
        setStatusOpen={setStatusOpen}
        setPostData={setPostData}
        setPosts={setPosts}
        setReset={setReset}
        closeConnection={closeConnection}
        setMachineId={setMachineId}
        initialPostData={initialPostData}
      />
      <Dialog
        style={
          machine?.Cover !== null
            ? { minHeight: "150px" }
            : { minHeight: "0px" }
        }
      >
        {!slackSearchResultsContext && (
          <CloseButton
            onClick={() => {
              if (event) {
                setStatusOpen(true);
              } else {
                setPostData(initialPostData);
                setPosts([]);
                setReset(true);
                closeConnection();
                setMachineId("");
                setLog(false);
              }
            }}
          >
            &#10006;
          </CloseButton>
        )}
        <Cover
          style={
            machine?.Cover !== null && machine?.Cover !== undefined
              ? { backgroundImage: `url(${img})`, minHeight: "150px" }
              : {}
          }
        >
          <Header
            style={
              // The close button is absolutely positioned in this corner, and it is only rendered
              // outside the slack view. Reserve its width so it cannot sit on top of the edit pen.
              !slackSearchResultsContext ? { paddingRight: "48px" } : undefined
            }
          >
            {backtoMenu && (
              <ArrowBackIosIcon
                onClick={backToMachines}
                style={{
                  height: "100%",
                  fontSize: "2rem",
                  cursor: "pointer",
                }}
              />
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                // Bounded, so a long machine name cannot push the edit pen past the right edge.
                flex: 1,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  minWidth: 0,
                }}
              >
                <span
                  title={`[${machine?.MachineCode ?? ""}] ${
                    machine?.MachineName ?? ""
                  }`}
                  style={{
                    // Clamped to two lines: machine names run long enough to shove anything
                    // beside them off a phone screen. The full name is in the title and in the
                    // edit popover.
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    wordBreak: "break-word",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {" "}
                  {`[${
                    machine?.MachineCode != null ? machine?.MachineCode : " "
                  }]`}
                  {machine?.MachineName}
                </span>
                <EditMachineButton
                  machineId={machineId}
                  onSaved={(saved) => {
                    setMachine((current) =>
                      current
                        ? {
                            ...current,
                            MachineName: saved.MachineName,
                            MachineCode: saved.MachineCode,
                          }
                        : current
                    );
                    // An edit writes a log post under the selected user. Without this the post
                    // only shows up after navigating away and back, because nothing else
                    // re-reads the open log. Clearing isInit re-runs the initial fetch below.
                    setPosts([]);
                    setActivePage(1);
                    setIsInit(false);
                  }}
                />
                <MachineQrCodeButton
                  machineId={machineId}
                  fallbackMachine={machine}
                />
              </span>
              {backtoMenu && (
                <BackButton onClick={backToMachines}>
                  <p
                    style={{
                      display: "flex",
                      alignItems: "center",
                      fontSize: "11px",
                      paddingTop: "10px",
                    }}
                  >
                    Back to machines
                  </p>
                </BackButton>
              )}
            </div>
          </Header>{" "}
        </Cover>
        <PostTimeline
          posts={posts}
          isLoading={isLoading}
          hasOlderPosts={!!machineId && activePage < totalPages}
          onLoadOlder={loadOlderPosts}
          contentOverflow={selectedImageData ? "hidden" : "visible"}
          style={{ marginBottom: isMobile ? "60px" : "0" }}
          renderPosts={(shownPosts) => (
            <PostsByDate posts={shownPosts} getMap={getMap} />
          )}
        />
        {!isBrowser && log && !selectedImageData && (
          <Drawer
            scrollToPost={undefined}
            key={id + "log"}
            isReadOnly={false}
            id={id}
            event={event}
            setEvent={setEvent}
          />
        )}
        {isBrowser && (
          <FormComponent
            isReadOnly={false}
            id={id}
            event={event}
            setEvent={setEvent}
          />
        )}
      </Dialog>
    </Overlay>
  );
};

export default Log;
